import { useUserData, type FlattenedUser } from '../api/queryHooks/user';
import { readImpersonation } from '../utils/jwt';
import type { Impersonation } from '../api/types';

export interface UseLoginResult {
  userData: FlattenedUser | null | undefined;
  isPending: boolean;
  /**
   * True while the session query is (re)fetching. A guard that would act on
   * a *negative* answer (not logged in) must wait for this to clear: when a
   * token arrives (login, guest minted by album-code redemption) `login()`
   * invalidates the query, and until the refetch lands the cached answer is
   * still the stale "anonymous" one. Redirecting on it bounces the fresh
   * session back to /login. A positive answer stays trustworthy meanwhile.
   */
  isFetching: boolean;
  isLoggedIn: boolean;
  isGuest: boolean;
  /** Non-null while an admin is viewing the shop as this user. */
  impersonation: Impersonation | null;
}

export const useLogin = (): UseLoginResult => {
  const { data: userData, isPending, isFetching } = useUserData();

  const isLoggedIn = userData !== null && typeof userData !== 'undefined';
  const isGuest = isLoggedIn && (userData as { guest?: boolean })?.guest === true;
  const impersonation = readImpersonation(userData?.token ?? null);

  return { userData, isPending, isFetching, isLoggedIn, isGuest, impersonation };
};
